import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  const navLinks = [
    { to: '/', label: '首页' },
    { to: '/analysis/an001', label: '数据分析' },
    { to: '/about', label: '关于我们' },
    { to: '/contact', label: '联系我们' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50 border-b border-background-200'
          : 'bg-background-50/85 backdrop-blur-sm border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center h-16 md:h-[68px] gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 rounded-md bg-primary-700 text-background-50 font-heading font-bold flex items-center justify-center text-sm">
              51
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-semibold text-foreground-950 text-base md:text-lg tracking-wide">
                51每日大赛
              </span>
              <span className="text-[11px] text-foreground-500 tracking-wide hidden sm:block">
                今日大赛聚集地
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            <Link
              to="/"
              className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-accent-600 font-medium'
                  : 'text-foreground-700 hover:text-primary-700'
              }`}
            >
              首页
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCatOpen((prev) => !prev)}
                className={`flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'text-accent-600 font-medium'
                    : 'text-foreground-700 hover:text-primary-700'
                }`}
              >
                赛事分类
                <i
                  className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${
                    catOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {catOpen ? (
                <div className="absolute left-0 top-full mt-2 w-64 bg-background-50 border border-background-200 rounded-lg p-2 animate-fade-in">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-primary-50 transition-colors"
                    >
                      <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`} />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm text-foreground-900">{cat.name}</span>
                        <span className="text-xs text-foreground-500">{cat.count} 场赛事</span>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  location.pathname.startsWith(link.to)
                    ? 'text-accent-600 font-medium'
                    : 'text-foreground-700 hover:text-primary-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden lg:flex items-center ml-auto">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索赛事、球队、关键词"
                className="w-56 xl:w-72 bg-background-100 border border-background-200 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="ml-2 px-4 py-2 text-sm rounded-md bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap pointer-events-auto"
            >
              搜索
            </button>
          </form>

          <Link
            to="/search"
            className="lg:hidden ml-auto w-9 h-9 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
            aria-label="搜索"
          >
            <i className="ri-search-line w-5 h-5 flex items-center justify-center" />
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-9 h-9 rounded-md flex items-center justify-center text-foreground-800 hover:bg-background-100 transition-colors"
            aria-label="菜单"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-3 flex flex-col gap-1">
            <form onSubmit={submitSearch} className="flex items-center gap-2 mb-2">
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索赛事、球队、关键词"
                className="flex-1 bg-background-100 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="px-4 py-2 text-sm rounded-md bg-accent-500 text-background-50 whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-1 pt-2 border-t border-background-200 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <i className={`${cat.icon} text-primary-600 w-4 h-4 flex items-center justify-center`} />
                  {cat.shortName}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}